/**
 * The three fields that connect a tenant's own Meta WhatsApp Business account,
 * and the one that is a bearer credential.
 *
 * Split out of `WhatsAppCard.tsx` by slice 16. The card became a tab panel with
 * a hierarchy — what stops a send at the top, expert settings behind a
 * disclosure — and a two-hundred-line form sitting in the middle of that file
 * made the hierarchy impossible to read in the source, which is the same
 * complaint the tenant made about reading it on screen.
 *
 * **The split changed one thing and it was a defect, not a decision.** The whole
 * instructional callout was gated on `existing === null`, which was survivable
 * while the old card's expiry warning carried the same instruction in its own
 * words; slice 16 replaced that warning's text with a "Replace token" button and
 * left the replace path with three fields and nothing saying what to paste into
 * them. The gate is now on the paragraph that really is first-connect-only —
 * whose account and whose bill — and the sentence naming a *system user* token,
 * Meta Business Settings and the two permissions renders on both paths. The
 * comment beside it says so, because the tidy-looking edit is to put the gate
 * back.
 *
 * **SECURITY, and it is the reason this file is worth reading before editing.**
 * The system user token exists here for the length of one submission and in one
 * place: the value of a `type="password"` input, cleared the moment the request
 * it was typed for has been answered. It is never put in a URL, a query string,
 * a toast, an error message or a log line, and no response shape on this screen
 * has a field it could come back in — `metaWhatsAppIntegrationSchema` is
 * `.strict()` and has no token field, so an API that started returning one would
 * fail parsing here as well as there.
 *
 * The token is *kept* on a validation failure and *dropped* on a request
 * failure, which looks inconsistent and is not. Nothing left the browser in the
 * first case, so the token has not been used and clearing it would punish a typo
 * in an id field. In the second the request was made, so the value has served
 * its purpose and does not outlive it — and the form says so before anyone
 * pastes anything, rather than after a failed retry.
 *
 * **`connectWhatsAppRequestSchema` is the validator**, parsed here and parsed
 * again by the API. Hand-written field checks in front of a contract are how a
 * form starts accepting something the endpoint refuses.
 */
import { Button, Callout, FormField, Input } from '@wend/ui';
import { useState, type FormEvent } from 'react';

import { FormAlert } from '../../components/FormAlert';
import { ROOT_FIELD, apiFieldErrors } from '../../lib/api/errors';
import {
  connectWhatsApp,
  connectWhatsAppRequestSchema,
  type WhatsAppCheckResponse,
  type WhatsAppIntegration,
} from '../../lib/api/whatsapp';
import { validate, withoutError, type FormErrors } from '../../lib/form';
import { RequestFailureAlert, requestFailure, type RequestFailure } from './request-failure';

export function WhatsAppConnectForm({
  existing,
  onConnected,
  onCancel,
}: {
  existing: WhatsAppIntegration | null;
  onConnected: (response: WhatsAppCheckResponse) => void;
  onCancel?: () => void;
}) {
  const [systemUserToken, setSystemUserToken] = useState('');
  const [wabaId, setWabaId] = useState(existing?.wabaId ?? '');
  const [phoneNumberId, setPhoneNumberId] = useState(existing?.phoneNumberId ?? '');
  const [errors, setErrors] = useState<FormErrors>({});
  const [failure, setFailure] = useState<RequestFailure | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    setFailure(null);

    const result = validate(connectWhatsAppRequestSchema, {
      systemUserToken,
      wabaId,
      phoneNumberId,
    });
    if (!result.ok) {
      // Nothing left the browser, so the token has not been used yet and is
      // kept: clearing it here would punish a typo in an id field.
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);

    let response: WhatsAppCheckResponse;
    try {
      response = await connectWhatsApp(result.data);
    } catch (error) {
      const fieldErrors = apiFieldErrors(error);
      delete fieldErrors[ROOT_FIELD];
      setErrors(fieldErrors);
      setFailure(requestFailure(error));
      // SECURITY: the request was made, so the token has served its purpose and
      // does not outlive it. A retry means pasting it again — said on the form.
      setSystemUserToken('');
      setPending(false);
      return;
    }

    setSystemUserToken('');
    onConnected(response);
  }

  return (
    <form
      onSubmit={(event) => {
        void onSubmit(event);
      }}
      noValidate
      autoComplete="off"
      className="flex max-w-xl flex-col gap-5"
    >
      {/*
        THE TOKEN INSTRUCTION IS NOT GATED ON `existing`, AND IT USED TO BE.

        Both paragraphs sat behind `existing === null`, so the replace path — the
        one somebody arrives on from a red "Access token — expired" callout, the
        single most likely reason this form is ever opened twice — offered three
        fields and no sentence saying what kind of credential to paste. The old
        card carried it in the expiry callout ("a new system user token from Meta
        Business Settings, with the same WABA and phone number ids"); slice 16
        replaced that callout's text with a Replace button and the sentence
        landed nowhere. Progressive means short by default, not deleted.

        The ownership paragraph stays gated: whose account this is, and whose
        bill, is a thing you decide once, and somebody replacing an expired token
        decided it months ago. What kind of token to mint is needed every time.
      */}
      <Callout
        tone="info"
        title={existing === null ? 'This is your own Meta account' : 'Use a system user token'}
      >
        {existing === null && (
          <p className="mb-2">
            Wend sends through your WhatsApp Business Account. The number, its quality rating and
            everything Meta decides to do about the sending — a limit cut, a review, a pause — belong
            to your account rather than Wend&rsquo;s, and Meta bills you for the conversations.
          </p>
        )}
        <p>
          Create a <span className="text-text">system user</span> token in Meta Business Settings
          with <span className="font-mono text-[0.82rem] text-text">whatsapp_business_messaging</span>{' '}
          and{' '}
          <span className="font-mono text-[0.82rem] text-text">whatsapp_business_management</span>. A
          personal user token is bound to someone&rsquo;s login and stops working the day they
          change their password.
        </p>
        {existing !== null && (
          <p className="mt-2">
            The two ids below are the ones already stored. Leave them as they are unless the number
            itself has changed — a replacement is all three fields or none.
          </p>
        )}
      </Callout>

      {failure !== null && <RequestFailureAlert failure={failure} />}
      {errors[ROOT_FIELD] !== undefined && <FormAlert tone="error">{errors[ROOT_FIELD]}</FormAlert>}

      <FormField
        label="System user token"
        required
        description="Encrypted before it is stored, and never shown again — not here, not by the API, not to Wend. It is cleared from this form the moment it is sent, so a retry means pasting it again."
        error={errors.systemUserToken}
      >
        <Input
          // Never echoed, never pre-filled, and never offered a stored value.
          type="password"
          value={systemUserToken}
          onChange={(event) => {
            setSystemUserToken(event.target.value);
            setErrors((current) => withoutError(current, 'systemUserToken'));
          }}
          autoComplete="off"
          spellCheck={false}
          className="font-mono text-[0.9rem]"
          disabled={pending}
        />
      </FormField>

      <FormField
        label="WhatsApp Business Account id"
        required
        description="The WABA id from WhatsApp Manager — a long number, not the business name."
        error={errors.wabaId}
      >
        <Input
          value={wabaId}
          onChange={(event) => {
            setWabaId(event.target.value);
            setErrors((current) => withoutError(current, 'wabaId'));
          }}
          autoComplete="off"
          spellCheck={false}
          inputMode="numeric"
          placeholder="102290129340398"
          className="font-mono text-[0.9rem]"
          disabled={pending}
        />
      </FormField>

      <FormField
        label="Phone number id"
        required
        description="Meta’s id for the sending number — also a long number. It is not the phone number itself, which Meta shows beside it."
        error={errors.phoneNumberId}
      >
        <Input
          value={phoneNumberId}
          onChange={(event) => {
            setPhoneNumberId(event.target.value);
            setErrors((current) => withoutError(current, 'phoneNumberId'));
          }}
          autoComplete="off"
          spellCheck={false}
          inputMode="numeric"
          placeholder="106540352242922"
          className="font-mono text-[0.9rem]"
          disabled={pending}
        />
      </FormField>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" loading={pending}>
          {pending ? 'Asking Meta' : existing === null ? 'Connect and test' : 'Replace and test'}
        </Button>

        {onCancel !== undefined && (
          <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={onCancel}>
            Cancel
          </Button>
        )}

        <p className="text-[0.85rem] text-muted">
          Saving checks the token against Meta straight away — including when it expires — so you
          find out here rather than at the first campaign.
        </p>
      </div>
    </form>
  );
}
